import { MaterialCommunityIcons } from '@expo/vector-icons';
import { useTranslation } from 'react-i18next';
import { Platform, StyleSheet, Text, View } from 'react-native';
import {
  RenderListItemPropsInterface,
} from 'react-native-dropdown-picker';
import { RadioButton, TextInput } from 'react-native-paper';
import { ControlledSelect } from '../../components/forms/select';
import { Colors } from '../../themes/colors';
import { MaterialCommunityIconName } from '../../utils/iconTypes';
import { useFormContext } from '../forms';
import { IssueReminderDropdownInput } from './issueReminderDropdownInput';
import { ReminderAlertInputs, ReminderAlertType } from './types';

type IssueReminderDropdownProps = {
  alertType: ReminderAlertType;
};

export const IssueReminderDropdown = ({
  alertType
}: IssueReminderDropdownProps) => {
  const { t } = useTranslation();

  const { control, getValues, setValue } = useFormContext();
  const notes = getValues(ReminderAlertInputs.notes);

  const reminderItems = [
    {
      label: t('alerts.onDate'),
      value: ReminderAlertType.Date,
    }, {
      label: t('alerts.onDays'),
      value: ReminderAlertType.Days
    }, {
      label: t('alerts.onDayInterval'),
      value: ReminderAlertType.DayInterval
    }
  ];

  const AlertItem = <T,>({
    label,
    isSelected,
    onPress,
    item,
  }: RenderListItemPropsInterface<T>) => {
    const handlePress = () => {
      onPress(item as ItemType<T>);
    };

    const radioButtonValue = (isSelected && 'checked') || 'unchecked';

    return (
      <View style={styles.listItem}>
        <Text style={styles.itemLabel}>{label}</Text>
        <RadioButton.Android
          value="test"
          status={radioButtonValue}
          uncheckedColor={Colors.grayDivider}
          onPress={handlePress}
          hitSlop={10}
        />
      </View>
    );
  };

  const chevronIcon = (name: MaterialCommunityIconName) => {
    return <MaterialCommunityIcons name={name} size={28} />;
  };

  return (
    <>
      <View style={[styles.sectionContainer]}>
        <Text style={styles.title}>{'Set Reminder Logic'}</Text>

        <ControlledSelect
          name={'alertType'}
          control={control}
          label={'Set Reminder Logic'}
          items={reminderItems}
          listMode="SCROLLVIEW"
          dropDownPickerProps={{
            scrollViewProps: {
              nestedScrollEnabled: true,
            },
            style: styles.dropdown,
            textStyle: styles.itemLabel,
            placeholderStyle: styles.placeholderLabel,
            dropDownContainerStyle: [
              styles.dropdownListContainer,
              { 
                minHeight: 235,
                maxHeight: 235,
              },
            ],
            ArrowDownIconComponent: () => chevronIcon('chevron-down'),
            ArrowUpIconComponent: () => chevronIcon('chevron-up'),
            renderListItem: (props) => <AlertItem {...props} />,
          }}
        />
        {alertType && <IssueReminderDropdownInput type={alertType} />}
      </View>

      {alertType && (
        <View
          style={[
            styles.sectionContainer,
            styles.hideBehindDropdown,
            { marginTop: 20 },
          ]}
        >
          <Text style={styles.title}>
            {t('issue.reminder.notesOptional')}
          </Text>
          <TextInput
            value={notes}
            onChangeText={(text) =>
              setValue(ReminderAlertInputs.notes, text, { shouldDirty: false })
            }
            mode="outlined"
            style={styles.textInput}
            contentStyle={styles.textInputContainer}
            outlineColor={Colors.grayText}
            activeOutlineColor={Colors.grayText}
            outlineStyle={{ borderWidth: 1 }}
            placeholder={t('common.optional')}
            placeholderTextColor={Colors.grayDisabled}
            multiline={true}
          />
        </View>
      )}
    </>
  );
};

const styles = StyleSheet.create({
  sectionContainer: {
    paddingHorizontal: 16,
    paddingVertical: 20,
    backgroundColor: Colors.white,
    borderRadius: 8,
    ...Platform.select({
      web: {
        width: '100%',
        minWidth: '100%',
        maxWidth: '100%',
      },
      ios: {},
      android: {},
    }),
  },
  title: {
    color: Colors.blackText,
    fontSize: 16,
    fontWeight: '600',
    letterSpacing: -0.25,
    marginBottom: 12,
  },
  dropdown: {
    borderWidth: 1,
    borderColor: Colors.grayText,
    height: 52,
    fontSize: 16,
    fontWeight: '400',
    ...Platform.select({
      web: {
        width: '100%',
        minWidth: '100%',
        maxWidth: '100%',
      },
      ios: {},
      android: {},
    }),
  },
  placeholderLabel: {
    fontSize: 16,
    fontWeight: '400',
    color: Colors.grayFill,
  },
  dropdownListContainer: {
    flexGrow: 1,
    borderWidth: 1,
    borderColor: Colors.grayDivider,
    backgroundColor: Colors.secondaryGrayHeader,
    marginTop: 8,
    borderTopStartRadius: 8,
    borderTopEndRadius: 8,
    paddingVertical: 20,
    paddingHorizontal: 12,
    position: 'relative',
    top: 0,
    zIndex: 1000,
    elevation: 5,
  },
  listItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    height: 52,
    paddingHorizontal: 12,
    backgroundColor: Colors.white,
    borderWidth: 1,
    borderColor: Colors.primaryGrayHeader,
    marginBottom: 12,
    borderRadius: 8,
  },
  itemLabel: {
    flex: 1,
    fontSize: 16,
    fontWeight: '400',
    color: Colors.blackText,
  },
  addAlertButton: {
    flexDirection: 'row',
    marginTop: 15,
    alignItems: 'center',
    columnGap: 5,
    maxWidth: '35%',
  },
  addAnotherLabel: {
    color: Colors.primaryDark,
    fontWeight: '500',
    fontSize: 14,
    letterSpacing: -0.25,
  },
  lineBreak: {
    borderWidth: 0.5,
    maxHeight: 1,
    borderColor: Colors.grayDivider,
    flex: 1,
  },
  optionalSettingsLabel: {
    fontSize: 14,
    fontWeight: '400',
    letterSpacing: -0.2,
    color: Colors.grayText,
    marginHorizontal: 10,
  },
  hideBehindDropdown: {
    zIndex: -1,
  },
  textInputContainer: {
    paddingHorizontal: 12,
    paddingTop: 10,
    paddingBottom: 10,
  },
  textInput: {
    height: 92,
    backgroundColor: Colors.white,
    marginBottom: 20,
  },
});
